import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { Bell, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Alerts() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState(null);

  const load = async () => { const { data } = await api.get("/alerts"); setAlerts(data); };
  useEffect(() => { load(); }, []);

  const ackedByMe = (a) => (a.acknowledged_by || []).some((x) => x.user_id === user.id);

  const acknowledge = async (id) => {
    await api.post(`/alerts/${id}/acknowledge`);
    toast.success("Acknowledged");
    load();
  };

  if (!alerts) return <Loading label="Loading alerts" />;
  const critical = alerts.filter((a) => a.requires_ack && !ackedByMe(a));

  return (
    <div className="max-w-4xl">
      <SectionTitle sub="Operational, equipment & training alerts">Alerts</SectionTitle>

      {critical.length > 0 && (
        <div className="sg-card p-4 mb-5" data-testid="critical-banner" style={{ borderColor: "var(--danger)", background: "rgba(239,68,68,0.08)" }}>
          <div className="font-head font-bold flex items-center gap-2" style={{ color: "var(--danger)" }}>
            <AlertTriangle size={18} /> {critical.length} critical alert(s) require acknowledgment
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3" data-testid="alerts-list">
        {alerts.length === 0 && <div className="sg-card p-6 text-sm" style={{ color: "var(--txt2)" }}>No active alerts.</div>}
        {alerts.map((a) => {
          const acked = ackedByMe(a);
          return (
            <div key={a.id} className="sg-card p-4 flex items-start gap-3" data-testid={`alert-${a.id}`}
              style={a.requires_ack && !acked ? { borderColor: "var(--danger)" } : {}}>
              <Bell size={20} style={{ color: a.requires_ack ? "var(--danger)" : "var(--accent)" }} />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap"><StatusChip status={a.level === "critical" || a.level === "override" ? "warning" : a.level} /><span className="font-head font-bold">{a.title}</span></div>
                <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>{a.body}</div>
                {(a.acknowledged_by || []).length > 0 && (
                  <div className="text-xs mt-2" style={{ color: "var(--txt2)" }}>Acknowledged by: {a.acknowledged_by.map((x) => `${x.user_name} (${x.role})`).join(", ")}</div>
                )}
              </div>
              {a.requires_ack && (
                acked ? <StatusChip status="ready" label="Acknowledged" />
                  : <button className="sg-btn" style={{ minHeight: 40 }} onClick={() => acknowledge(a.id)} data-testid={`ack-${a.id}`}><CheckCircle2 size={15} /> Acknowledge</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
